import { useState } from 'react';
import Cookies from 'js-cookie';

const usePostAnalysis = () => {
    const [error, setError] = useState(null);

    const postAnalysisToAPI = async (
        cvFile,
        mLetterfile,
        linkedInURL,
        linkedInData,
        analysisResults,
        careerPreferences,
        reflection
    ) => {
        try {
            const cvBase64 = cvFile ? await convertFileToBase64(cvFile) : null;
            let mlBase64 = null;

            if (mLetterfile && typeof mLetterfile === 'object' && mLetterfile instanceof File) {
                mlBase64 = await convertFileToBase64(mLetterfile);
            }

            let formattedLinkedInURL = "https://www.linkedin.com/";
            if (linkedInURL && linkedInURL.trim() !== '') {
                formattedLinkedInURL = linkedInURL.startsWith('http://') || linkedInURL.startsWith('https://') 
                    ? linkedInURL 
                    : 'https://' + linkedInURL;
            }

            const transformedAnalysis = {
                summary: {
                    keyTraits: analysisResults.summary?.keyTraits || [],
                    dominantTraits: analysisResults.summary?.dominantTraits || "",
                    jobMatch: analysisResults.summary?.jobMatch || "",
                    hiringInsights: analysisResults.summary?.hiringInsights || "",
                    careerAdvice: analysisResults.summary?.careerAdvice || ""
                },
                bigFive: {
                    openness: {
                        score: analysisResults.bigFive?.openness?.score || 0,
                        level: analysisResults.bigFive?.openness?.level || "",
                        interpretation: analysisResults.bigFive?.openness?.interpretation || "",
                        developmentTip: analysisResults.bigFive?.openness?.developmentTip || "Consider exploring new ideas and technologies to expand your skillset.",
                        workplaceImpact: analysisResults.bigFive?.openness?.workplaceImpact || "Brings creative thinking and innovation to projects and teams."
                    },
                    conscientiousness: {
                        score: analysisResults.bigFive?.conscientiousness?.score || 0,
                        level: analysisResults.bigFive?.conscientiousness?.level || "",
                        interpretation: analysisResults.bigFive?.conscientiousness?.interpretation || "",
                        developmentTip: analysisResults.bigFive?.conscientiousness?.developmentTip || "Consider setting clear goals and maintaining structured workflows.",
                        workplaceImpact: analysisResults.bigFive?.conscientiousness?.workplaceImpact || "Contributes reliability and organization to projects and teams."
                    },
                    extraversion: {
                        score: analysisResults.bigFive?.extraversion?.score || 0,
                        level: analysisResults.bigFive?.extraversion?.level || "",
                        interpretation: analysisResults.bigFive?.extraversion?.interpretation || "",
                        developmentTip: analysisResults.bigFive?.extraversion?.developmentTip || "Consider balancing social interaction with focused independent work.",
                        workplaceImpact: analysisResults.bigFive?.extraversion?.workplaceImpact || "Affects communication style and energy in collaborative settings."
                    },
                    agreeableness: {
                        score: analysisResults.bigFive?.agreeableness?.score || 0,
                        level: analysisResults.bigFive?.agreeableness?.level || "",
                        interpretation: analysisResults.bigFive?.agreeableness?.interpretation || "",
                        developmentTip: analysisResults.bigFive?.agreeableness?.developmentTip || "Consider balancing cooperation with assertiveness when needed.",
                        workplaceImpact: analysisResults.bigFive?.agreeableness?.workplaceImpact || "Influences team dynamics and conflict resolution approaches."
                    },
                    neuroticism: {
                        score: analysisResults.bigFive?.neuroticism?.score || 0,
                        level: analysisResults.bigFive?.neuroticism?.level || "",
                        interpretation: analysisResults.bigFive?.neuroticism?.interpretation || "",
                        developmentTip: analysisResults.bigFive?.neuroticism?.developmentTip || "Consider developing stress management techniques for challenging situations.",
                        workplaceImpact: analysisResults.bigFive?.neuroticism?.workplaceImpact || "Affects resilience and emotional stability in high-pressure situations."
                    },
                    workBehavior: analysisResults.bigFive?.workBehavior || "Analysis of work behavior not provided.",
                    teamFitInsight: analysisResults.bigFive?.teamFitInsight || "Team fit analysis not provided.",
                    careerGrowthTip: analysisResults.bigFive?.careerGrowthTip || "Focus on leveraging strengths while developing areas for improvement."
                },
                disc: {
                    dominance: {
                        score: analysisResults.disc?.dominance?.score || 0,
                        interpretation: analysisResults.disc?.dominance?.interpretation || "",
                        workplaceImpact: analysisResults.disc?.dominance?.workplaceImpact || "Affects leadership style and decision-making approach.",
                        developmentTip: analysisResults.disc?.dominance?.developmentTip || "Balance assertiveness with active listening and collaboration."
                    },
                    influence: {
                        score: analysisResults.disc?.influence?.score || 0,
                        interpretation: analysisResults.disc?.influence?.interpretation || "",
                        workplaceImpact: analysisResults.disc?.influence?.workplaceImpact || "Impacts communication style and persuasiveness in team settings.",
                        developmentTip: analysisResults.disc?.influence?.developmentTip || "Focus on developing structured communication that balances enthusiasm with clarity."
                    },
                    steadiness: {
                        score: analysisResults.disc?.steadiness?.score || 0,
                        interpretation: analysisResults.disc?.steadiness?.interpretation || "",
                        workplaceImpact: analysisResults.disc?.steadiness?.workplaceImpact || "Contributes consistency and reliability to team dynamics.",
                        developmentTip: analysisResults.disc?.steadiness?.developmentTip || "Develop comfort with change and flexibility when required."
                    },
                    conscientiousness: {
                        score: analysisResults.disc?.conscientiousness?.score || 0,
                        interpretation: analysisResults.disc?.conscientiousness?.interpretation || "",
                        workplaceImpact: analysisResults.disc?.conscientiousness?.workplaceImpact || "Brings attention to detail and quality standards to work.",
                        developmentTip: analysisResults.disc?.conscientiousness?.developmentTip || "Balance perfectionism with practical outcomes and timelines."
                    },
                    primaryStyle: analysisResults.disc?.primaryStyle || "Primary DISC style not determined.",
                    workStyleSummary: analysisResults.disc?.workStyleSummary || "Work style summary not provided."
                },
                mbti: {
                    type: analysisResults.mbti?.type || "",
                    dimensions: {
                        extraversion_introversion: analysisResults.mbti?.dimensions?.extraversion_introversion || {
                            preference: "E",
                            score: 50,
                            interpretation: "No data available",
                            workImpact: "No data available",
                            teamDynamics: "No data available"
                        },
                        sensing_intuition: analysisResults.mbti?.dimensions?.sensing_intuition || {
                            preference: "S",
                            score: 50,
                            interpretation: "No data available",
                            workImpact: "No data available",
                            teamDynamics: "No data available"
                        },
                        thinking_feeling: analysisResults.mbti?.dimensions?.thinking_feeling || {
                            preference: "T",
                            score: 50,
                            interpretation: "No data available",
                            workImpact: "No data available",
                            teamDynamics: "No data available"
                        },
                        judging_perceiving: analysisResults.mbti?.dimensions?.judging_perceiving || {
                            preference: "J",
                            score: 50,
                            interpretation: "No data available",
                            workImpact: "No data available",
                            teamDynamics: "No data available"
                        }
                    },
                    workStyle: analysisResults.mbti?.workStyle || "Work style analysis not provided.",
                    strengths: analysisResults.mbti?.strengths || [],
                    challenges: analysisResults.mbti?.challenges || [],
                    teamDynamics: analysisResults.mbti?.teamDynamics || "Team dynamics analysis not provided.",
                    communicationPreferences: analysisResults.mbti?.communicationPreferences || ["Clear and concise communication", "Structured information"],
                    careerAlignments: analysisResults.mbti?.careerAlignments || {
                        roleTypes: ["Technical roles", "Creative positions"],
                        workEnvironments: ["Collaborative settings", "Innovation-focused environments"],
                        developmentAreas: ["Leadership skills", "Communication techniques"]
                    }
                },
                riasec: {
                    realistic: {
                        score: analysisResults.riasec?.realistic?.score || 0,
                        description: analysisResults.riasec?.realistic?.description || "",
                        jobAlignment: analysisResults.riasec?.realistic?.jobAlignment || "Aligns with technical and hands-on roles.",
                        careerDevelopment: analysisResults.riasec?.realistic?.careerDevelopment || "Consider developing practical skills through training and certification."
                    },
                    investigative: {
                        score: analysisResults.riasec?.investigative?.score || 0,
                        description: analysisResults.riasec?.investigative?.description || "",
                        jobAlignment: analysisResults.riasec?.investigative?.jobAlignment || "Matches with analytical and research-focused positions.",
                        careerDevelopment: analysisResults.riasec?.investigative?.careerDevelopment || "Focus on developing problem-solving and analytical capabilities."
                    },
                    artistic: {
                        score: analysisResults.riasec?.artistic?.score || 0,
                        description: analysisResults.riasec?.artistic?.description || "",
                        jobAlignment: analysisResults.riasec?.artistic?.jobAlignment || "Suitable for creative and design-oriented roles.",
                        careerDevelopment: analysisResults.riasec?.artistic?.careerDevelopment || "Explore opportunities to apply creativity in professional settings."
                    },
                    social: {
                        score: analysisResults.riasec?.social?.score || 0,
                        description: analysisResults.riasec?.social?.description || "",
                        jobAlignment: analysisResults.riasec?.social?.jobAlignment || "Aligns with collaborative and people-focused positions.",
                        careerDevelopment: analysisResults.riasec?.social?.careerDevelopment || "Consider developing interpersonal and communication skills."
                    },
                    enterprising: {
                        score: analysisResults.riasec?.enterprising?.score || 0,
                        description: analysisResults.riasec?.enterprising?.description || "",
                        jobAlignment: analysisResults.riasec?.enterprising?.jobAlignment || "Matches with leadership and business-oriented roles.",
                        careerDevelopment: analysisResults.riasec?.enterprising?.careerDevelopment || "Focus on developing leadership and initiative-taking abilities."
                    },
                    conventional: {
                        score: analysisResults.riasec?.conventional?.score || 0,
                        description: analysisResults.riasec?.conventional?.description || "",
                        jobAlignment: analysisResults.riasec?.conventional?.jobAlignment || "Suitable for structured and detail-oriented positions.",
                        careerDevelopment: analysisResults.riasec?.conventional?.careerDevelopment || "Consider developing organizational and process-focused skills."
                    },
                    primaryType: analysisResults.riasec?.primaryType || "",
                    secondaryType: analysisResults.riasec?.secondaryType || "",
                    suggestedCareers: analysisResults.riasec?.suggestedCareers || [
                        "Software Developer",
                        "UX/UI Designer",
                        "Web Developer",
                        "Product Manager",
                        "Digital Creative Director"
                    ]
                },
                recommendations: {
                    dos: analysisResults.recommendations?.dos || [],
                    donts: analysisResults.recommendations?.donts || [],
                    bestWorkCulture: analysisResults.recommendations?.bestWorkCulture || "",
                    developmentPoints: analysisResults.recommendations?.developmentPoints || [],
                    managementStrategies: analysisResults.recommendations?.managementStrategies || [
                        "Provide clear expectations and goals",
                        "Offer regular feedback and recognition",
                        "Encourage autonomy and innovation",
                        "Support professional development"
                    ],
                    onboardingTips: analysisResults.recommendations?.onboardingTips || [
                        "Provide comprehensive technical orientation",
                        "Introduce to team members individually",
                        "Set clear expectations and goals",
                        "Establish regular check-ins for feedback"
                    ],
                    interviewFocusAreas: analysisResults.recommendations?.interviewFocusAreas || [
                        "Technical expertise and problem-solving skills",
                        "Creative thinking and innovation",
                        "Team collaboration and communication",
                        "Project management experience",
                        "Adaptability and learning attitude"
                    ],
                    jobFitAnalysis: analysisResults.recommendations?.jobFitAnalysis || {
                        overallScore: 85,
                        confidenceLevel: "high",
                        traitContributions: {
                            conscientiousness: 34,
                            extraversion: 13,
                            openness: 16,
                            neuroticism: 22
                        },
                        interpretation: "Strong overall fit for technical and creative roles.",
                        strengthAreas: ["Technical problem-solving", "Creative design"],
                        developmentAreas: ["Team communication", "Process management"],
                        riskAreas: "May need support with highly routine tasks or overly social environments."
                    },
                    predictiveValidity: analysisResults.recommendations?.predictiveValidity || {
                        ffm: {
                            level: "high",
                            score: 85,
                            primaryUse: "Job Performance Prediction",
                            confidence: "High research-backed validity",
                            limitations: "May vary across job types"
                        },
                        riasec: {
                            level: "high",
                            score: 80,
                            primaryUse: "Career Interest Alignment",
                            confidence: "Good predictive value for job satisfaction",
                            limitations: "Less direct performance prediction"
                        },
                        disc: {
                            level: "moderate",
                            score: 70,
                            primaryUse: "Team Dynamics",
                            confidence: "Useful for work behavior patterns",
                            limitations: "Limited performance prediction"
                        },
                        mbti: {
                            level: "moderate",
                            score: 65,
                            primaryUse: "Team Communication",
                            confidence: "Better for understanding preferences",
                            limitations: "Not validated for performance prediction"
                        }
                    }
                }
            };

            // Handle LinkedIn data - provide defaults if missing
            const defaultLinkedInData = {
                firstName: "",
                lastName: "",
                headline: "",
                industryName: "",
                locationName: "",
                experience: [],
                skills: [],
                languages: [],
                displayPictureUrl: ""
            };

            const processedLinkedInData = linkedInData ? {
                ...defaultLinkedInData,
                ...linkedInData
            } : defaultLinkedInData;

            const imageSize = (processedLinkedInData?.img_800_800 != null ? processedLinkedInData.img_800_800 : 
                             processedLinkedInData?.img_400_400 != null ? processedLinkedInData.img_400_400 :
                             processedLinkedInData?.img_200_200 != null ? processedLinkedInData.img_200_200 :
                             processedLinkedInData?.img_100_100 != null ? processedLinkedInData.img_100_100 : "");

            const questionnairePayload = {
                careerPreferences: {
                    desiredRole: careerPreferences?.desiredRole || "",
                    selectedWorkStyles: careerPreferences?.selectedWorkStyles || [],
                    selectedMotivators: careerPreferences?.selectedMotivators || [],
                    workType: careerPreferences?.workType || "",
                    relocation: careerPreferences?.relocation || ""
                },
                reflection: {
                    ambition: reflection?.ambition || "",
                    ambitionStrategy: reflection?.ambitionStrategy || "",
                    greatestSuccess: reflection?.greatestSuccess || "",
                    fitnessRituals: reflection?.fitnessRituals || ""
                }
            };

            const questionnaireResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/report/questionnaire`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${Cookies.get('token')}`,
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify(questionnairePayload)
            });
            
            if (!questionnaireResponse.ok) {
                const errorData = await questionnaireResponse.json();
                throw new Error(errorData.message || 'Failed to post questionnaire');
            }
            
            const questionnaireData = await questionnaireResponse.json();
            const questionnaire_id = questionnaireData.data.questionnaire_id;

            const requestBody = {
                CvFile: cvBase64 ? {
                    base64: cvBase64.base64,
                    filename: cvFile.name
                } : {
                    base64: "",
                    filename: "no_cv.txt"
                },
                linkedInURL: formattedLinkedInURL,
                linkedInData: {
                    firstName: processedLinkedInData.firstName || "",
                    lastName: processedLinkedInData.lastName || "",
                    headline: processedLinkedInData.headline || "",
                    industryName: processedLinkedInData.industryName || "",
                    locationName: processedLinkedInData.locationName || "",
                    experience: (processedLinkedInData.experience || []).map(exp => ({
                        title: exp.title || "",
                        company: exp.company || "",
                        start_date: exp.start_date || "",
                        end_date: exp.end_date || ""
                    })),
                    skills: processedLinkedInData.skills || [],
                    languages: (processedLinkedInData.languages || []).map(lang => ({
                        name: lang.name || "",
                        proficiency: lang.proficiency || ""
                    })),
                    displayPictureUrl: processedLinkedInData.displayPictureUrl ? 
                        `${processedLinkedInData.displayPictureUrl}${imageSize}` : ""
                },
                Analysis: transformedAnalysis,
                questionnaire_id,
                careerPreferences: careerPreferences || {},
                reflection: reflection || {}
            };

            // Handle motivation letter
            if (mlBase64) {
                requestBody.MLetterFile = {
                    base64: mlBase64.base64,
                    filename: mLetterfile.name
                };
            } else {
                requestBody.MLetterFile = {
                    base64: "",
                    filename: "no_motivation_letter.txt"
                };
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report/new`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${Cookies.get('token')}`,
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('API Error Response:', errorData);

                if (response.status === 422) {
                    const errorCount = errorData.detail ? errorData.detail.length : 0;
                    throw new Error(`Validation error: ${errorCount} fields are missing or invalid. Check console for details.`);
                } else if (response.status === 401) {
                    throw new Error('Authentication error: Please log in again');
                } else {
                    throw new Error(errorData.message || 'Failed to post analysis to API');
                }
            }

            return await response.json();
        } catch (error) {
            console.error('Error posting analysis to API:', error);
            setError(error.message);
            throw new Error('Failed to post analysis to API: ' + error.message);
        }
    };

    return { postAnalysisToAPI, error };
};

const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve({
                base64: base64String,
                filename: file.name
            });
        };
        reader.onerror = (error) => reject(error);
    });
};

export default usePostAnalysis;